import TextField from '@mui/material/TextField';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {StaticDatePicker} from '@mui/x-date-pickers/StaticDatePicker';
import {StaticDateRangePicker} from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import React, {useContext, useState} from "react";
import {Box, Button, Chip, Grid, useTheme} from "@mui/material";
import {encode} from 'js-base64';
import Typography from "@mui/material/Typography";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {hu} from "date-fns/locale";
import {formatHuman} from "../../utils/dateUtils";
import ReservationContext from "../../context/ReservationContext";
import useReservationQueries from "../../hooks/queries/useReservationQueries";
import {isRangeFullyAvailable} from "../../utils/reservationAvailability";
import {useSnackbar} from "notistack";
import Loading from "../Loading";


const Slot = ({time, ...rest}) => {
    const theme = useTheme()
    return <Chip
        {...rest}
        label={time.start + '-' + time.end}
        style={{
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.primary['20'],
            fontWeight: '600',
        }}/>
}

const NoAvailableTime = () => <Box sx={{width: '100%', justifyContent: 'center', alignItems: 'center', alignSelf: 'center'}}>
    <Typography variant="subtitle1" align="center">Erre a napra nincsenek szabad időpontok!</Typography>
    <Typography variant="body2" gutterBottom align="center">Válassz másik időpontot!</Typography>
</Box>


const AvailableTime = ({slots, proceed, selectedDate}) => {
    return <>
        <Typography variant="subtitle2" align="center">Szabad időpontok - {formatHuman(selectedDate)}</Typography>
        <Typography variant="body2" gutterBottom align="center">Válassz szabad időpontot az alábbiak közül!</Typography>
        <Grid container spacing={1}>
            {slots?.map((s, i) => <Grid style={{display: 'flex', justifyContent: 'center'}} item xs={4} xl={4} key={`slot_${i}`}>
                <Slot time={s} onClick={() => proceed(s)}/>
            </Grid>)}
        </Grid>
    </>
}

const FullDayInfo = () => <Box sx={{width: '100%', justifyContent: 'center', alignItems: 'center', alignSelf: 'center'}}>
    <Typography variant="subtitle2" align="center">Válassz egy szabad napot a naptárból!</Typography>
    <Typography variant="body2" gutterBottom align="center">A foglalás a nap egészére vonatkozik.</Typography>
</Box>

const RangeSelection = ({rangeStart, rangeEnd, onConfirm, onReset}) => {
    if (!rangeStart) return <Box sx={{width: '100%', justifyContent: 'center', alignItems: 'center', alignSelf: 'center'}}>
        <Typography variant="subtitle2" align="center">Válaszd ki az időszak kezdő napját!</Typography>
        <Typography variant="body2" gutterBottom align="center">Majd válaszd ki a záró napot is a naptárból.</Typography>
    </Box>

    if (!rangeEnd) return <Box sx={{width: '100%', justifyContent: 'center', alignItems: 'center', alignSelf: 'center'}}>
        <Typography variant="subtitle2" align="center">Kezdő nap: {formatHuman(rangeStart)}</Typography>
        <Typography variant="body2" gutterBottom align="center">Válaszd ki a záró napot is a naptárból!</Typography>
    </Box>

    return <Box sx={{width: '100%', justifyContent: 'center', alignItems: 'center', alignSelf: 'center'}}>
        <Typography variant="subtitle2" align="center">Kiválasztott időszak</Typography>
        <Typography variant="body1" gutterBottom align="center">{formatHuman(rangeStart)} - {formatHuman(rangeEnd)}</Typography>
        <Box sx={{display: 'flex', justifyContent: 'center', gap: 1}}>
            <Button variant="contained" onClick={onConfirm}>Tovább</Button>
            <Button onClick={onReset}>Újraválasztás</Button>
        </Box>
    </Box>
}


const SelectDate = () => {
    const today = new Date()
    const {next, selected} = useContext(ReservationContext)
    const lengthOptions = selected?.service?.length_options || 'regular'
    const [selectedDate, setSelectedDate] = useState(new Date())
    const [dateRange, setDateRange] = useState([null, null])
    const [month, setMonth] = useState(today.getMonth() + 1)
    const [year, setYear] = useState(today.getFullYear())
    const [loading, setLoading] = useState(false)
    const {enqueueSnackbar} = useSnackbar()

    const {useFreeSlotsForDate, useMonthlyAvailability, fetchMonthlyAvailability} = useReservationQueries()

    const {data: slots, } = useFreeSlotsForDate(selected?.price?.id, formatHuman(selectedDate))
    const {data: availability} = useMonthlyAvailability(selected?.price?.id, year, month)

    const toUriSafe = (s) => {
        let encoded = encode(formatHuman(selectedDate) + '|' + s.start + '|' + s.end);
        return encoded.replace('==', '');
    }

    const toUriSafeRange = (start, end) => {
        let encoded = encode(`${formatHuman(start)} 00:00:00|${formatHuman(end)} 23:59:59`);
        return encoded.replace('==', '');
    }

    const isAvailableForReserve = (date) => {
        if (availability === undefined) return false
        if (Object.keys(availability).includes(formatHuman(date))) return !availability[formatHuman(date)]
        return false
    }

    const proceed = (d) => next({...selected, date: toUriSafe(d)})

    const handleDateChange = (newValue) => {
        setSelectedDate(newValue)
        if (lengthOptions === 'full-day') next({...selected, date: toUriSafeRange(newValue, newValue)})
    }

    const handleRangeChange = async (newRange) => {
        setDateRange(newRange)
        const [start, end] = newRange
        if (!start || !end) return
        setLoading(true)
        const ok = await isRangeFullyAvailable(fetchMonthlyAvailability, selected?.price?.id, start, end)
            .catch(e => {
                console.error(e)
                return false
            })
            .finally(() => setLoading(false))
        if (!ok) {
            enqueueSnackbar('A kiválasztott időszakban foglalt nap található, válassz másik időszakot!', {variant: 'error'})
            setDateRange([null, null])
        }
    }

    const resetRange = () => setDateRange([null, null])

    const proceedRange = async () => {
        const [start, end] = dateRange
        if (!start || !end) return
        setLoading(true)
        const ok = await isRangeFullyAvailable(fetchMonthlyAvailability, selected?.price?.id, start, end)
            .catch(e => {
                console.error(e)
                return false
            })
            .finally(() => setLoading(false))
        if (ok) next({...selected, date: toUriSafeRange(start, end)})
        else {
            enqueueSnackbar('A kiválasztott időszakban időközben foglalás történt, válassz másik időszakot!', {variant: 'error'})
            setDateRange([null, null])
        }
    }

    const monthChange = (d) => {
        const selectedMonth = d.getMonth() + 1
        const selectedYear = d.getFullYear()
        setMonth(selectedMonth)
        setYear(selectedYear)
    }

    return <div className="data-container">
        <div className="date-select-container">
            <div className="date-select-calendar">
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
                    {lengthOptions === 'multi-day' ? (
                        <StaticDateRangePicker
                            displayStaticWrapperAs="desktop"
                            calendars={1}
                            disablePast
                            shouldDisableDate={isAvailableForReserve}
                            value={dateRange}
                            onChange={handleRangeChange}
                            onMonthChange={(d) => monthChange(d)}
                            renderInput={(startProps, endProps) => <>
                                <TextField {...startProps} />
                                <TextField {...endProps} />
                            </>}
                        />
                    ) : (
                        <StaticDatePicker
                            displayStaticWrapperAs="desktop"
                            disablePast
                            openTo="day"
                            shouldDisableDate={isAvailableForReserve}
                            value={selectedDate}
                            onChange={handleDateChange}
                            onMonthChange={(d) => monthChange(d)}
                            onYearChange={(d) => monthChange(d)}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    )}
                </LocalizationProvider>
            </div>
            <div className="data-container-slots" style={lengthOptions === 'regular' ? (slots?.length !== 0 ? {} : {display: 'flex'}) : {display: 'flex'}}>
                {lengthOptions === 'regular' && (slots?.length !== 0 ? <AvailableTime slots={slots} proceed={proceed} selectedDate={selectedDate}/>: <NoAvailableTime/>)}
                {lengthOptions === 'full-day' && <FullDayInfo/>}
                {lengthOptions === 'multi-day' && <RangeSelection rangeStart={dateRange[0]} rangeEnd={dateRange[1]} onConfirm={proceedRange} onReset={resetRange}/>}
            </div>
        </div>

        <Loading isLoading={loading}/>
    </div>
}

export default SelectDate
